/**
 * StatsScene - shows the player's history and aggregate performance.
 *
 * Pulls from localStorage (no live game state needed). Sections:
 *   - Daily summary: games, win %, streaks, best score
 *   - 28-day calendar mini-grid showing each day's outcome
 *   - Adventure best
 *   - Score distribution histogram (first-attempt scores only)
 *
 * Accessed from the menu via a "📊 stats" link. Esc / a back button returns
 * to the menu.
 */

import Phaser from 'phaser';
import { VIEWPORT_PX_W, VIEWPORT_PX_H } from '../config';
import { load, type SaveState } from '../persistence/saveState';
import {
  overallStats,
  recentCalendar,
  scoreHistogram,
  type CalendarDay,
  type HistogramBucket,
  type OverallStats,
} from '../persistence/stats';

const C = {
  bg: 0x0d0d10,
  panel: 0x1a1a20,
  panelBorder: 0x33333a,
  textPrimary: '#e8e8e8',
  textMuted: '#8a8a90',
  textAccent: '#e8c87a',
  textWin: '#7adb6b',
  // Calendar cell colors
  cellWon: 0x2c8a3d,        // green
  cellAttempted: 0x8a5a2c,  // amber
  cellSkipped: 0x2a2a2e,    // dark gray
  cellToday: 0x5aa8e8,      // blue (today-unplayed)
  cellBorder: 0x000000,
  histoBar: 0x5aa8e8,
} as const;

export class StatsScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Stats' });
  }

  create(): void {
    const w = VIEWPORT_PX_W;
    const h = VIEWPORT_PX_H;
    const state = load();

    this.add.rectangle(w / 2, h / 2, w, h, C.bg);

    // Title
    this.add
      .text(w / 2, 26, 'STATS', {
        fontFamily: 'monospace',
        fontSize: '28px',
        fontStyle: 'bold',
        color: C.textAccent,
      })
      .setOrigin(0.5, 0);

    let cursorY = 80;
    const stats = overallStats(state);
    cursorY = this.renderSummary(stats, cursorY);
    cursorY = this.renderCalendar(recentCalendar(state, 28), cursorY + 18);
    cursorY = this.renderAdventure(state, cursorY + 24);
    cursorY = this.renderHistogram(scoreHistogram(state), cursorY + 16);

    // Back button + Esc hotkey
    this.buildBackButton();
    this.input.keyboard?.on('keydown', (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' || ev.key === 'Enter' || ev.key === ' ') {
        this.scene.start('Menu');
      }
    });
  }

  // --- Section renderers ------------------------------------------------

  private renderSummary(s: OverallStats, y: number): number {
    const w = VIEWPORT_PX_W;
    this.sectionTitle(y, 'DAILY');
    const lines = [
      `games played   ${s.daysPlayed}`,
      `wins           ${s.daysWon}  (${s.winPercent}%)`,
      `current streak ${s.currentStreak}${s.currentStreak > 0 ? ' 🔥' : ''}`,
      `best streak    ${s.bestStreak}`,
      `total attempts ${s.totalAttempts}`,
      `best score     ${s.bestEverScore}`,
    ];
    const text = this.add
      .text(w / 2, y + 24, lines.join('\n'), {
        fontFamily: 'monospace',
        fontSize: '13px',
        color: C.textPrimary,
        lineSpacing: 5,
        align: 'left',
      })
      .setOrigin(0.5, 0);
    return y + 24 + text.height;
  }

  private renderCalendar(days: CalendarDay[], y: number): number {
    const w = VIEWPORT_PX_W;
    this.sectionTitle(y, 'LAST 28 DAYS');

    const cols = 14;
    const rows = Math.ceil(days.length / cols);
    const cellSize = 22;
    const gap = 4;
    const gridW = cols * cellSize + (cols - 1) * gap;
    const startX = (w - gridW) / 2;
    const startY = y + 24;

    for (let i = 0; i < days.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = startX + col * (cellSize + gap) + cellSize / 2;
      const cy = startY + row * (cellSize + gap) + cellSize / 2;
      const color = this.calendarCellColor(days[i]!.status);
      const rect = this.add.rectangle(cx, cy, cellSize, cellSize, color);
      rect.setStrokeStyle(1, C.cellBorder, 0.4);
      // Tooltip on tap: brief label of date + status
      rect.setInteractive({ useHandCursor: true });
      rect.on('pointerdown', () => this.flashTooltip(days[i]!));
    }

    // Legend
    const legendY = startY + rows * (cellSize + gap) + 8;
    const legendItems: [string, number][] = [
      ['won', C.cellWon],
      ['attempted', C.cellAttempted],
      ['skipped', C.cellSkipped],
      ['today', C.cellToday],
    ];
    let lx = startX;
    for (const [label, color] of legendItems) {
      this.add.rectangle(lx, legendY, 10, 10, color).setOrigin(0, 0.5);
      this.add
        .text(lx + 14, legendY, label, {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: C.textMuted,
        })
        .setOrigin(0, 0.5);
      lx += 14 + label.length * 6 + 14;
    }

    return legendY + 12;
  }

  private renderAdventure(state: SaveState, y: number): number {
    const w = VIEWPORT_PX_W;
    this.sectionTitle(y, 'ADVENTURE');
    const a = state.adventureBest;
    const lines =
      a.bestTotal <= 0
        ? ['no runs yet']
        : [
            `best total      ${a.bestTotal}`,
            `highest level   ${a.highestLevel} / 7`,
          ];
    const text = this.add
      .text(w / 2, y + 24, lines.join('\n'), {
        fontFamily: 'monospace',
        fontSize: '13px',
        color: a.bestTotal <= 0 ? C.textMuted : C.textPrimary,
        lineSpacing: 5,
      })
      .setOrigin(0.5, 0);
    return y + 24 + text.height;
  }

  private renderHistogram(buckets: HistogramBucket[], y: number): number {
    const w = VIEWPORT_PX_W;
    this.sectionTitle(y, 'FIRST-ATTEMPT SCORES');

    const total = buckets.reduce((s, b) => s + b.count, 0);
    if (total === 0) {
      this.add
        .text(w / 2, y + 30, 'no games played yet', {
          fontFamily: 'monospace',
          fontSize: '12px',
          color: C.textMuted,
        })
        .setOrigin(0.5, 0);
      return y + 48;
    }

    const maxCount = Math.max(...buckets.map((b) => b.count));
    const barAreaW = w - 80;
    const labelW = 76;
    const startX = (w - (labelW + barAreaW)) / 2;
    const rowH = 14;
    const startY = y + 26;

    buckets.forEach((b, i) => {
      const ry = startY + i * rowH;
      const label = b.to === Infinity ? `${b.from}+` : `${b.from}-${b.to}`;
      this.add
        .text(startX, ry + rowH / 2, label, {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: C.textMuted,
        })
        .setOrigin(0, 0.5);
      const barW = maxCount > 0 ? (b.count / maxCount) * barAreaW : 0;
      if (barW > 0) {
        this.add
          .rectangle(startX + labelW, ry + rowH / 2, barW, rowH - 4, C.histoBar)
          .setOrigin(0, 0.5);
      }
      this.add
        .text(startX + labelW + barW + 4, ry + rowH / 2, String(b.count), {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: b.count > 0 ? C.textPrimary : C.textMuted,
        })
        .setOrigin(0, 0.5);
    });

    return startY + buckets.length * rowH;
  }

  // --- Helpers ---------------------------------------------------------

  private sectionTitle(y: number, label: string): void {
    const w = VIEWPORT_PX_W;
    this.add
      .text(w / 2, y, label, {
        fontFamily: 'monospace',
        fontSize: '11px',
        fontStyle: 'bold',
        color: C.textAccent,
        backgroundColor: '#00000088',
      })
      .setOrigin(0.5, 0)
      .setPadding(8, 3, 8, 3);
  }

  private calendarCellColor(status: CalendarDay['status']): number {
    switch (status) {
      case 'won': return C.cellWon;
      case 'attempted': return C.cellAttempted;
      case 'today-unplayed': return C.cellToday;
      case 'skipped': return C.cellSkipped;
    }
  }

  private tooltipText?: Phaser.GameObjects.Text;
  private tooltipFade?: Phaser.Time.TimerEvent;

  /** Show a brief floating tooltip with the day's date and best score. */
  private flashTooltip(day: CalendarDay): void {
    const w = VIEWPORT_PX_W;
    const labelMap: Record<CalendarDay['status'], string> = {
      won: 'won',
      attempted: 'attempted',
      skipped: 'no play',
      'today-unplayed': 'today',
    };
    const text =
      day.bestScore > 0
        ? `${day.dateKey}  ${labelMap[day.status]}  ${day.bestScore} pts`
        : `${day.dateKey}  ${labelMap[day.status]}`;
    if (this.tooltipText) this.tooltipText.destroy();
    if (this.tooltipFade) this.tooltipFade.remove();
    this.tooltipText = this.add
      .text(w / 2, VIEWPORT_PX_H - 50, text, {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: C.textPrimary,
        backgroundColor: '#000000cc',
      })
      .setOrigin(0.5, 0.5)
      .setPadding(8, 4, 8, 4)
      .setDepth(200);
    this.tooltipFade = this.time.delayedCall(2200, () => {
      if (this.tooltipText) {
        this.tooltipText.destroy();
        this.tooltipText = undefined;
      }
    });
  }

  /** Real DOM back button (same reliability story as the in-game buttons). */
  private buildBackButton(): void {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = '← BACK';
    btn.setAttribute('aria-label', 'Back to menu');
    btn.style.position = 'absolute';
    btn.style.zIndex = '10';
    btn.style.padding = '0';
    btn.style.background = 'rgba(0,0,0,0.55)';
    btn.style.color = '#ffffff';
    btn.style.border = '1px solid rgba(255,255,255,0.25)';
    btn.style.borderRadius = '4px';
    btn.style.fontFamily = 'monospace';
    btn.style.fontWeight = 'bold';
    btn.style.cursor = 'pointer';
    btn.style.touchAction = 'manipulation';
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.scene.start('Menu');
    });

    const parent =
      (this.game.canvas.parentElement as HTMLElement | null) ?? document.body;
    if (parent.style.position === '' || parent.style.position === 'static') {
      parent.style.position = 'relative';
    }
    parent.appendChild(btn);

    const place = () => {
      const canvas = this.game.canvas;
      const cr = canvas.getBoundingClientRect();
      const pr = (btn.offsetParent as HTMLElement | null)?.getBoundingClientRect() ?? {
        left: 0, top: 0,
      };
      const sx = cr.width / VIEWPORT_PX_W;
      const sy = cr.height / VIEWPORT_PX_H;
      const bw = 80 * sx;
      const bh = 26 * sy;
      btn.style.width = `${bw}px`;
      btn.style.height = `${bh}px`;
      btn.style.fontSize = `${11 * sy}px`;
      btn.style.left = `${cr.left + 8 * sx - pr.left}px`;
      btn.style.top = `${cr.top + 6 * sy - pr.top}px`;
    };
    place();
    this.scale.on('resize', place);

    // Remove on scene shutdown so it doesn't leak across scenes.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (btn.parentElement) btn.parentElement.removeChild(btn);
    });
  }
}
